import {getPixelsFromRem, Show, VStack} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {useEffect, useId, useMemo, useState} from 'react';
import {OnChangeValue} from 'react-select';
import AsyncReactSelect from 'react-select/async';

import {defaultTo, equals, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps, usePrevious} from 'shared/web';

import {FormControlProps} from '../../../types/FormControlProps';
import {OptionTypeBase} from '../../../types/OptionTypeBase';
import {HelperText} from '../../HelperText/HelperText';
import {Label} from '../../Label/Label';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import {ChoiceCommonProps} from '../types';
import {selectComponents} from '../utils/components';
import {formatOptionFieldLabel} from '../utils/formatOptionFieldLabel';
import {reactSelectStyles} from '../utils/reactSelectStyles';
import {ChoiceProvider} from './ChoiceProvider';

export interface AsyncChoiceProps<ValueType>
  extends FormControlProps<ValueType | null>,
    TestIdProps,
    Omit<ChoiceCommonProps<ValueType, ValueType>, 'options'> {
  defaultOptions?: boolean;
  loadOptions?:
    | ((inputValue: string) => Promise<ReadonlyArray<OptionTypeBase<ValueType | Nullish>>>)
    | undefined;
  loadLabels?:
    | ((value: ValueType) => Promise<ReadonlyArray<OptionTypeBase<ValueType | Nullish>>>)
    | undefined;
}

export function AsyncChoice<ValueType>(props: AsyncChoiceProps<ValueType>) {
  const theme = useTheme();
  const t = useTranslationContext();
  const id = useId();
  // contains options that were loaded in last call of props.loadOptions
  const [loadedOptions, setLoadedOptions] = useState<
    ReadonlyArray<OptionTypeBase<ValueType | Nullish>>
  >([]);
  // stores final options that are sent to AsyncReactSelect as selected values
  const [optionByValue, setOptionByValue] = useState<
    ReadonlyArray<OptionTypeBase<ValueType | Nullish>> | OptionTypeBase<ValueType | Nullish> | null
  >(null);

  // just use props.loadOptions and set loadedOptions
  const finalLoadOptions = (query: string) => {
    if (!props.loadOptions) {
      return;
    }

    return props.loadOptions(query).then((options) => {
      setLoadedOptions(options);
      return options;
    });
  };

  const prevValue = usePrevious(props.value);
  useEffect(() => {
    if (isNotNil(props.value) && !equals(props.value, prevValue)) {
      if (props.loadLabels) {
        props.loadLabels(props.value).then((labels) => {
          setOptionByValue(labels);
        });
      }
    }
  }, [props.value, loadedOptions, prevValue, props.loadLabels]);

  const handleChange = (value: OnChangeValue<OptionTypeBase<ValueType | Nullish>, false>) => {
    if (!value?.value) {
      props.onChange?.(null);
      return;
    }

    props.onChange?.(value.value);
  };

  const getOptionsByValue = (value: ValueType | Nullish) => {
    if (isNil(value)) {
      return null;
    }

    return loadedOptions?.find((o) => o.value === value);
  };

  // This is a workaround for an issue in react-select, when using custom components
  // https://github.com/JedWatson/react-select/issues/5489
  const components = useMemo(() => selectComponents<ValueType, false>(), []);

  return (
    <VStack>
      <Label
        id={id}
        isRequired={props.isRequired}
        tooltip={props.tooltip}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      <ChoiceProvider
        options={{
          'data-testid': props['data-testid'],
          hasOptionCheckbox: props.hasOptionCheckbox,
        }}
      >
        <AsyncReactSelect
          inputId={id}
          defaultOptions
          loadOptions={props.loadOptions ? finalLoadOptions : undefined}
          value={optionByValue}
          name={props.name}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onChange={handleChange}
          components={components}
          isDisabled={props.isDisabled}
          styles={reactSelectStyles<ValueType, false>(theme)}
          formatOptionLabel={props.formatOptionLabel ?? formatOptionFieldLabel}
          defaultValue={getOptionsByValue(props.defaultValue)}
          menuPortalTarget={props.menuInPortal ? document.body : undefined}
          placeholder={defaultTo(t('general.labels.select'), props.placeholder)}
          isClearable={!props.isNotClearable}
          isSearchable={props.isSearchable ?? true}
          menuPlacement={props.menuPlacement ?? 'auto'}
          maxMenuHeight={getPixelsFromRem(props.maxMenuHeight)}
          noOptionsMessage={props.noOptionsMessage}
          closeMenuOnScroll={props.closeMenuOnScroll}
          closeMenuOnSelect={props.closeMenuOnSelect}
          isLoading={props.isLoading}
          filterOption={props.filterOption}
        />
      </ChoiceProvider>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}
